import React, { FC, useMemo } from 'react';
import { useTranslation } from 'hooks';
import Icon from 'components/ui/icon';
import PageTemplate from 'components/pageTemplate';
import CustomScrollbar from 'components/ui/customScrollbar';
import Link from 'components/ui/link';
import { CredentialsCard } from './components/CredentialsCard';
import { BoardingStage } from 'types/BoardingStage';
import Utils from 'helpers/Utils';
import { OnboardingInformer } from './components/OnboardingInformer';
import { Skeleton } from 'components/ui/skeleton';
import { CommercialOfferWasAccepted } from './components/DafStatusInformer/CommercialOfferWasAccepted';
import { DafIsReady } from './components/DafStatusInformer/DafIsReady';
import { DocumentsWereSubmitted } from './components/DafStatusInformer/DocumentsWereSubmitted';
import { firstSteps, technicalDocumentation } from './config';
import { DocLinksList } from 'types/Api';

interface Props {
  email: string;
  boardingStage: BoardingStage;
  isLoadingDocLinks: boolean;
  docLinks: DocLinksList;
  limits: { text: string; icon: string; current: number; max: number }[];
  isLoadingLimits: boolean;
  currentLegalEntityId: number;
}

export const GetStartedPage: FC<Props> = ({
  boardingStage,
  email,
  isLoadingDocLinks,
  docLinks,
  isLoadingLimits,
  limits,
  currentLegalEntityId,
}) => {
  const getTranslate = useTranslation();
  const displayOnboardingInformer = [
    'onboarding_draft',
    'onboarding_fill',
    'onboarding_review',
  ].includes(boardingStage);

  const statusComponents = {
    onboarding_draft: <CommercialOfferWasAccepted email={email} />,
    onboarding_fill: <DafIsReady currentLegalEntityId={currentLegalEntityId} />,
    onboarding_review: (
      <DocumentsWereSubmitted currentLegalEntityId={currentLegalEntityId} />
    ),
  };

  const renderWidget = useMemo(() => {
    return (
      <div className='max-tablet-l:flex-col flex items-start gap-5'>
        {statusComponents[boardingStage] ||
          statusComponents['onboarding_draft']}
        <CredentialsCard />
      </div>
    );
  }, [boardingStage]);

  const renderLimits = useMemo(() => {
    return (
      <div className='flex flex-col gap-5'>
        <h2 className='font-heading text-heading-m'>
          {getTranslate('getStarted.testMode.label')}
        </h2>
        <div className='max-desktop-s:grid-cols-2 max-mobile:grid-cols-1 grid grid-cols-4 gap-5'>
          {limits.map((data) => (
            <div key={Utils.getHash()} className='card relative w-full p-5'>
              {isLoadingLimits ? (
                <Skeleton height={50} />
              ) : (
                <div className='flex items-center gap-2'>
                  <div>
                    <Icon
                      name={data.icon}
                      size={18}
                      className='text-icon-status-success-primary bg-icon-status-success-secondary rounded-full p-2'
                    />
                  </div>
                  <div className='flex h-full flex-col gap-2'>
                    <h2 className='font-heading text-heading-m'>
                      {data.current}/{data.max}
                    </h2>
                    <p className='text-text-secondary text-m-medium'>
                      {getTranslate(data.text)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }, [limits, isLoadingLimits]);

  const renderFirstSteps = useMemo(() => {
    const { label, items } = firstSteps;

    return (
      <div className='flex flex-col gap-5'>
        <h2 className='font-heading text-heading-m'>{getTranslate(label)}</h2>
        <div className='max-mobile:flex-col flex gap-5'>
          {items.map(({ id, image, label, link, text }) => {
            const card = (
              <div className='card w-full p-5'>
                <div className='flex flex-col gap-4'>
                  {isLoadingDocLinks ? (
                    <Skeleton width='70%' height={24} />
                  ) : (
                    <>
                      <div className='bg-background-base absolute top-5 right-5 flex h-[30px] w-[30px] items-center justify-center rounded-full'>
                        <Icon
                          name={
                            id === 'testCards'
                              ? 'im-External-Link'
                              : 'im-Arrow-right-Option-2'
                          }
                          size={14}
                          className='text-brand-main'
                        />
                      </div>
                      <div className='inline-flex'>{image}</div>
                    </>
                  )}

                  <h3 className='font-heading text-heading-sm text-text-primary'>
                    {isLoadingDocLinks ? (
                      <Skeleton width='100%' height={18} />
                    ) : (
                      getTranslate(label)
                    )}
                  </h3>
                  <p className='text-text-secondary text-m-medium'>
                    {isLoadingDocLinks ? (
                      <Skeleton width='80%' height={14} />
                    ) : (
                      getTranslate(text)
                    )}
                  </p>
                </div>
              </div>
            );

            if (id === 'testCards') {
              return (
                <a
                  href={docLinks?.[link]}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='inline-flex flex-1'
                  key={id}>
                  {card}
                </a>
              );
            }

            return (
              <Link url={link} key={id} className='inline-flex flex-1'>
                {card}
              </Link>
            );
          })}
        </div>
      </div>
    );
  }, [docLinks, isLoadingDocLinks]);

  const renderDocumentation = useMemo(() => {
    const { label, items } = technicalDocumentation;

    return (
      <div className='flex flex-col gap-5'>
        <h2 className='font-heading text-heading-m'>{getTranslate(label)}</h2>
        <div className='max-desktop-s:grid-cols-2 max-mobile:grid-cols-1 grid grid-cols-4 gap-5'>
          {items.map(({ id, image, label, link, text }) => {
            return (
              <a
                href={docLinks?.[link]}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex'
                key={id}>
                <div className='card relative w-full p-5'>
                  <div className='flex h-full flex-col justify-between gap-6'>
                    <div className='flex flex-col gap-4'>
                      {isLoadingDocLinks ? (
                        <Skeleton width='70%' height={24} />
                      ) : (
                        <>
                          <div>
                            <Icon
                              name={image}
                              size={21}
                              className='text-brand-main'
                            />
                          </div>
                          <div className='bg-background-base absolute top-5 right-5 flex h-[30px] w-[30px] items-center justify-center rounded-full'>
                            <Icon
                              name='im-External-Link'
                              size={14}
                              className='text-brand-main'
                            />
                          </div>
                        </>
                      )}

                      <h3 className='font-heading text-heading-sm text-text-primary'>
                        {isLoadingDocLinks ? (
                          <Skeleton width='100%' height={18} />
                        ) : (
                          getTranslate(label)
                        )}
                      </h3>
                      <p className='text-text-secondary text-m-medium'>
                        {isLoadingDocLinks ? (
                          <Skeleton width='80%' height={14} />
                        ) : (
                          getTranslate(text)
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    );
  }, [docLinks, isLoadingDocLinks]);

  return (
    <CustomScrollbar>
      {displayOnboardingInformer ? <OnboardingInformer email={email} /> : null}
      <div className='mx-auto w-full max-w-[1200px]'>
        <PageTemplate.Container>
          <div className='flex flex-col gap-10'>
            {renderWidget}
            {renderLimits}
            {renderFirstSteps}
            {renderDocumentation}
          </div>
        </PageTemplate.Container>
      </div>
    </CustomScrollbar>
  );
};
